/*
 * Web Mercator tile maths for AppTrackMap.vue.
 *
 * WHY THIS FILE EXISTS. No map library is on the approved dependency list
 * (vue-spec libraries.md), and a capture console needs exactly one thing from a map:
 * show me where this vehicle was. That is a static, fitted picture — no panning, no
 * zooming, no clustering — which is about sixty lines of arithmetic and a grid of
 * <img> tags. Pulling in Leaflet for it would be a dependency, a stylesheet and a
 * lifecycle to keep in step with Vue, in exchange for interactions this screen does
 * not offer.
 *
 * Everything here is pure: latitudes and longitudes in, pixel offsets out. The
 * component owns the DOM and the container size; this file owns the projection.
 *
 * IT ALSO OWNS NO TILE ADDRESS. A placement says which tile (z/x/y) and where it
 * goes, and the caller turns that into a src — because where the imagery comes from
 * is now a setting (stores/mapSources.ts: OSM direct, or TomTom through fleet-api)
 * and a traffic overlay is the SAME placements fetched from a second address. One
 * layout, several layers; the arithmetic below stays free of both.
 *
 * THE PROJECTION is the standard slippy-map one (EPSG:3857). At zoom z the whole
 * world is a square of TILE_SIZE * 2^z pixels, tiled into 256px squares, with x
 * running east from longitude -180 and y running south from the Mercator limit.
 */

export const TILE_SIZE = 256

/*
 * The deepest zoom this console will ask OSM for.
 *
 * 17 is close enough to see which side of a street a van stopped on, and stopping
 * there keeps a single stationary device — whose bounding box is a point, so the fit
 * would otherwise run to zoom 19 — from requesting the most expensive tiles on the
 * server for no extra information.
 */
export const MAX_ZOOM = 17

/** The Mercator projection is undefined at the poles; this is where it is cut off. */
const MAX_LATITUDE = 85.05112878

/**
 * OpenStreetMap's own tiles, fetched straight from them — what the console draws
 * unless a map source has been configured. Filled in by stores/mapSources.ts, which
 * is the one place that decides where a tile's imagery comes from.
 */
export const TILE_URL_TEMPLATE = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'

/** Required by the OSM tile usage policy, and rendered over every map. */
export const OSM_ATTRIBUTION = '© OpenStreetMap contributors'

export interface LatLng {
    latitude:  number
    longitude: number
}

/** A track point as the map takes it: a coordinate, plus the heading if it has one. */
export interface HeadedLatLng extends LatLng {
    angle?: number
}

export interface Bounds {
    minLatitude:  number
    maxLatitude:  number
    minLongitude: number
    maxLongitude: number
}

export interface Point {
    x: number
    y: number
}

/**
 * One tile to render, already placed in the container's pixel space. `x` is wrapped
 * around the antimeridian and is what an address is built from; `key` keeps the
 * unwrapped column, so two tiles a world apart stay two elements.
 */
export interface TilePlacement {
    key:  string
    z:    number
    x:    number
    y:    number
    left: number
    top:  number
}

export interface MapView {
    zoom:   number
    width:  number
    height: number
    tiles:  TilePlacement[]
    /** Where a coordinate lands inside the container, in pixels from its top-left. */
    project: (latitude: number, longitude: number) => Point
}

export interface MapViewOptions {
    /** Pixels kept clear of the container edge when fitting. */
    padding?: number
    maxZoom?: number
    /*
     * A zoom the caller insists on, instead of the one that fits the points — this is
     * how a person zooms in. The fitted zoom is still what they started from, so a
     * view is always the track's own view plus what they did to it.
     */
    zoom?: number
    /** Pixels the view is dragged by, positive east and south. */
    panX?: number
    panY?: number
}

/** World-pixel x of a longitude at the given zoom. */
export const longitudeToWorldX = (longitude: number, zoom: number): number =>
    ((longitude + 180) / 360) * TILE_SIZE * 2 ** zoom

/** World-pixel y of a latitude at the given zoom. */
export const latitudeToWorldY = (latitude: number, zoom: number): number => {
    const clamped = Math.min(MAX_LATITUDE, Math.max(-MAX_LATITUDE, latitude))
    const sin     = Math.sin((clamped * Math.PI) / 180)

    return (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * TILE_SIZE * 2 ** zoom
}

/** The smallest box containing every point, or null when there are none. */
export const boundsOf = (points: LatLng[]): Bounds | null => {
    if (points.length === 0) return null

    return points.reduce<Bounds>((box, point) => ({
        minLatitude:  Math.min(box.minLatitude,  point.latitude),
        maxLatitude:  Math.max(box.maxLatitude,  point.latitude),
        minLongitude: Math.min(box.minLongitude, point.longitude),
        maxLongitude: Math.max(box.maxLongitude, point.longitude),
    }), {
        minLatitude:  points[0].latitude,
        maxLatitude:  points[0].latitude,
        minLongitude: points[0].longitude,
        maxLongitude: points[0].longitude,
    })
}

/**
 * The deepest zoom at which the box still fits the container.
 *
 * Counted down from maxZoom rather than solved in closed form: the two axes scale
 * differently under Mercator, the answer is an integer, and there are at most
 * eighteen candidates.
 */
export const fitZoom = (
    bounds:  Bounds,
    width:   number,
    height:  number,
    padding  = 24,
    maxZoom  = MAX_ZOOM,
): number => {
    const usableWidth  = Math.max(1, width  - padding * 2)
    const usableHeight = Math.max(1, height - padding * 2)

    for (let zoom = maxZoom; zoom > 0; zoom--) {
        const spanX = longitudeToWorldX(bounds.maxLongitude, zoom) - longitudeToWorldX(bounds.minLongitude, zoom)
        // y grows southwards, so the MINIMUM latitude is the LARGER y.
        const spanY = latitudeToWorldY(bounds.minLatitude, zoom) - latitudeToWorldY(bounds.maxLatitude, zoom)

        if (spanX <= usableWidth && spanY <= usableHeight) return zoom
    }

    return 0
}

/**
 * Everything the component needs to paint: the zoom, the tiles that cover the
 * container, and a projection from coordinates to container pixels.
 *
 * Returns null for an empty point set or a container that has not been measured yet
 * — both are ordinary states (a device with no fix, a tab that has just mounted),
 * not errors.
 */
export const createMapView = (
    points:  LatLng[],
    width:   number,
    height:  number,
    options: MapViewOptions = {},
): MapView | null => {
    const bounds = boundsOf(points)

    if (!bounds || width <= 0 || height <= 0) return null

    const padding = options.padding ?? 24
    const zoom    = options.zoom ?? fitZoom(bounds, width, height, padding, options.maxZoom ?? MAX_ZOOM)

    // The box's centre in world pixels, and the offset that puts it in the middle of
    // the container. Everything else is that offset applied.
    const centreX = (longitudeToWorldX(bounds.minLongitude, zoom) + longitudeToWorldX(bounds.maxLongitude, zoom)) / 2
    const centreY = (latitudeToWorldY(bounds.minLatitude, zoom) + latitudeToWorldY(bounds.maxLatitude, zoom)) / 2

    const originX = centreX - width / 2 + (options.panX ?? 0)
    const originY = centreY - height / 2 + (options.panY ?? 0)

    const tileCount = 2 ** zoom
    const tiles: TilePlacement[] = []

    const firstTileX = Math.floor(originX / TILE_SIZE)
    const lastTileX  = Math.floor((originX + width - 1) / TILE_SIZE)
    const firstTileY = Math.floor(originY / TILE_SIZE)
    const lastTileY  = Math.floor((originY + height - 1) / TILE_SIZE)

    for (let tileY = firstTileY; tileY <= lastTileY; tileY++) {
        // Above the north edge or below the south edge there is no tile — the map
        // simply ends, which is what the container's own background then shows.
        if (tileY < 0 || tileY >= tileCount) continue

        for (let tileX = firstTileX; tileX <= lastTileX; tileX++) {
            // x wraps around the antimeridian; the POSITION keeps the unwrapped
            // value so the strip stays continuous, only the address is wrapped.
            const wrappedX = ((tileX % tileCount) + tileCount) % tileCount

            tiles.push({
                key:  `${zoom}/${tileX}/${tileY}`,
                z:    zoom,
                x:    wrappedX,
                y:    tileY,
                left: tileX * TILE_SIZE - originX,
                top:  tileY * TILE_SIZE - originY,
            })
        }
    }

    return {
        zoom,
        width,
        height,
        tiles,
        project: (latitude: number, longitude: number): Point => ({
            x: longitudeToWorldX(longitude, zoom) - originX,
            y: latitudeToWorldY(latitude, zoom) - originY,
        }),
    }
}
